<template>
  <div>
    <!-- Stats -->
    <div class="grid grid-cols-3 gap-4 mb-6">
      <div class="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div class="text-2xl font-bold text-slate-800 font-mono">
          {{ categories.length }}
        </div>
        <div class="text-xs text-slate-400 mt-1">Catégories disponibles</div>
      </div>
      <div class="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div class="text-2xl font-bold text-emerald-600 font-mono">
          {{ categoriesActives }}
        </div>
        <div class="text-xs text-slate-400 mt-1">Actives</div>
      </div>
      <div class="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div class="text-2xl font-bold text-red-500 font-mono">
          {{ categoriesInactives }}
        </div>
        <div class="text-xs text-slate-400 mt-1">Non ajoutées</div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loadingGet" class="flex items-center justify-center py-24 text-slate-400 text-sm">
      <i class="fas fa-spinner fa-spin mr-1"></i>
      Chargement...
    </div>

    <!-- Tableau -->
    <div v-else class="bg-white border border-slate-200 rounded-xl overflow-hidden">

      <!-- En-tête tableau -->
      <div class="grid grid-cols-[48px_1fr_100px_100px_130px] gap-3 px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div></div>
        <div class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Catégorie</div>
        <div class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Fluide</div>
        <div class="text-xs font-semibold text-slate-400 uppercase tracking-wider">Action</div>
      </div>

      <!-- Lignes -->
      <div
        v-for="(cat, index) in categories"
        :key="cat.id"
        class="grid grid-cols-[48px_1fr_100px_100px_130px] gap-3 px-5 py-3.5 items-center border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150"
        :class="!cat.is_added ? 'opacity-50 hover:opacity-100 duration-200' : ''"
      >
        <!-- Icône -->
        <div class="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          <img :src="cat.icon" :alt="cat.label" class="w-5 h-5 object-contain" />
        </div>

        <!-- Nom -->
        <div>
          <div class="text-sm font-medium text-slate-800">{{ cat.label }}</div>
        </div>

        <!-- Fluide -->
        <div>
          <span
            class="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
            :class="cat.has_fluid ? 'bg-orange-50 text-orange-600' : 'bg-slate-100 text-slate-400'"
          >
            <span class="w-1.5 h-1.5 rounded-full" :class="cat.has_fluid ? 'bg-orange-500' : 'bg-slate-400'"></span>
            {{ cat.has_fluid ? 'Frigorigène' : 'Non' }}
          </span>
        </div>

        <!-- Action : toggle -->
        <div>
          <div
            class="inline-flex rounded-lg overflow-hidden border text-xs font-medium transition-all duration-150"
            :class="loadingAdd || loadingRemove ? 'opacity-50 pointer-events-none' : ''"
          >
            <button
              class="px-3 py-1.5 transition-colors duration-150 flex items-center"
              :class="cat.is_added
                ? 'bg-emerald-500 text-white cursor-default'
                : 'bg-white text-slate-400 hover:bg-green-100 hover:text-green-600 border-r border-slate-200'"
              :disabled="cat.is_added"
              @click="!cat.is_added && askToggle(cat)"
            >
              <i v-if="cat.is_added" class="fas fa-check mr-1" style="font-size: 10px"></i>
              <template v-if="!cat.is_added">
                Activer
              </template> 
              <template v-else>
                Actif
              </template>
            </button>
            <button
              class="px-3 py-1.5 transition-colors duration-150"
              :class="!cat.is_added
                ? 'bg-slate-400 text-white cursor-default'
                : 'bg-white text-slate-400 hover:bg-red-100 hover:text-red-600 border-l border-slate-200'"
              :disabled="!cat.is_added"
              @click="cat.is_added && askToggle(cat)"
            >
              <template v-if="!cat.is_added">
                Inactif
              </template> 
              <template v-else>
                Retirer
              </template>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- Modal de confirmation -->
    <div
      v-if="modal.show"
      class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-2xl p-7 w-full max-w-sm shadow-2xl shadow-slate-500">

        <h3 class="text-base font-semibold text-slate-800">
          {{ modal.type === 'add' ? 'Activer la catégorie' : 'Désactiver la catégorie' }}
        </h3>
        <p class="text-sm text-slate-500 mt-1.5 leading-relaxed">
          {{ modal.type === 'add'
            ? 'Voulez-vous ajouter cette catégorie à votre société ?'
            : 'Voulez-vous désactiver cette catégorie de votre société ?' }}
        </p>

        <!-- Aperçu catégorie -->
        <div v-if="modal.cat" class="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 mt-4">
          <img :src="modal.cat.icon" :alt="modal.cat.label" class="w-8 h-8 object-contain" />
          <div class="text-sm font-medium text-slate-800">{{ modal.cat.label }}</div>
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-2.5 mt-6">
          <button
            class="text-sm font-medium px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            @click="closeModal"
          >
            Annuler
          </button>
          <button
            class="text-sm font-medium px-4 py-2 rounded-lg text-white transition-colors"
            :class="modal.type === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'"
            @click="confirmAction"
          >
            {{ modal.type === 'add' ? 'Activer' : 'Désactiver' }}
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: "ParametreCategorieAppareil",
  data() {
    return {
      categories: [],
      loadingGet: false,
      loadingAdd: false,
      loadingRemove: false,
      modal: {
        show: false,
        type: null,
        cat: null,
      },
    }
  },
  computed: {
    categoriesActives() {
      return this.categories.filter(c => c.is_added).length
    },
    categoriesInactives() {
      return this.categories.filter(c => !c.is_added).length
    },
  },
  methods: {
    async getCategories() {
      this.loadingGet = true
      const response = await this.$request.post('categorie-appareil/all-categories')
      this.categories = response
      this.loadingGet = false
    },

    async addCategorie(key) {
      this.loadingAdd = true
      const response = await this.$request.post('categorie-appareil/add-categorie', { key })
      if (response == 1) await this.getCategories()
      this.loadingAdd = false
    },

    async removeCateg(key) {
      this.loadingRemove = true
      const response = await this.$request.post('categorie-appareil/remove-categorie', { key })
      if (response == 1) await this.getCategories()
      this.loadingRemove = false
    },

    askToggle(cat) {
      this.modal = { show: true, type: cat.is_added ? 'remove' : 'add', cat }
    },

    closeModal() {
      this.modal = { show: false, type: null, cat: null }
    },

    async confirmAction() {
      const { type, cat } = this.modal
      this.closeModal()
      if (type === 'add') {
        await this.addCategorie(cat.key)
      } else {
        await this.removeCateg(cat.key)
      }
    },
  },
  mounted() {
    this.getCategories()
  },
}
</script>